import axios from 'axios';
import { storage, auth } from '../../firebase/firebaseConfig';
import { ref, deleteObject, uploadBytes } from 'firebase/storage';

const authToken = async () => await auth.currentUser.getIdToken(true);

export default {
    // ------------------------------------------------------ //
    resetEnterprisePassword: async (enterpriseName, email) => {
        return await authToken().then(async (token) => {
            const config = {
                headers: { Authorization: `Bearer ${token}`}
            };
            
            return await axios.put(`/api/enterprise/reset`, {enterprise: enterpriseName, email: email}, config);
        });
    },

    // ------------------------------------------------------ //
    // ---------------------GET ROUTES----------------------- //
    // ------------------------------------------------------ //

    getEnterpriseData: async () => {
        return await authToken().then(async (token) => {
            const config = {
                headers: { Authorization: `Bearer ${token}`}
            };

            return await axios.get(`/api/enterprise/`, config);
        });
    },

    getEnterpriseAccount: async (uid) => {
        return await authToken().then(async (token) => {
            const config = {
                headers: { Authorization: `Bearer ${token}`}
            };

            return await axios.post(`/api/enterprise/accounts/get`, { uid }, config);
        });
    },

    getEnterpriseBrands: async () => {
        return await authToken().then(async (token) => {
            const config = {
                headers: { Authorization: `Bearer ${token}`}
            };

            return await axios.get(`/api/enterprise/themes`, config);
        });
    },

    getEnterpriseUser: async (uid) => {
        return await authToken().then(async (token) => {
            const config = {
                headers: { Authorization: `Bearer ${token}`}
            };

            return await axios.post(`/api/enterprise/users/manage`, { uid }, config);
        });
    },

    getEnterpriseAnalytics: async (enterpriseName, offset) => {
        return await authToken().then(async (token) => {
            const config ={
                headers: {
                    Authorization: `Bearer ${token}`,
                    enterprise: enterpriseName,
                    offset: offset
                }
            };

            return await axios.get(`/api/entstats/all`, config);
        });
    },

    getRangeEnterpriseAnalytics: async(enterpriseName, startDate, endDate) => {
        return await authToken().then(async token => {
            const config ={
                headers: {
                    Authorization: `Bearer ${token}`,
                    enterprise: enterpriseName,
                    start: startDate,
                    end: endDate
                }
            };

            return await axios.get(`/api/entstats/range`, config);
        });
    },
    
    getEvent: async (event_id) => {
        return await authToken().then(token => {
            const config = {
                headers: { Authorization: `Bearer ${token}`}
            };

            return axios.get(`/api/enterprise/event/${event_id}`, config);
        });
    },

    getEnterpriseHostedEvents: async () => {
        return await authToken().then((token) => {
            const config = {
                headers: { Authorization: `Bearer ${token}`}
            };

            return axios.get(`/api/enterprise/events`, config);
        });
    },

    getEnterpriseEventPointTotal: async (event_id) => {
        return await authToken().then(token => {
            const config = {
                headers: { Authorization: `Bearer ${token}`}
            };

            return axios.get(`/api/enterprise/event/points/${event_id}`, config);
        });
    },

    getRoleByName: async (roleName) => {
        return await authToken().then(token => {
            const config = {
                headers: { Authorization: `Bearer ${token}`}
            };

            return axios.get(`/api/enterprise/roles/${roleName}`, config);
        });
    },

    getBrandQr: async (enterprise) => await axios.get(`/api/enterprise/qr/${enterprise}`),

    // ------------------------------------------------------ //
    // --------------------END GET ROUTES-------------------- //
    // ------------------------------------------------------ //

    // ------------------------------------------------------ //
    // ----------------------PUT ROUTES---------------------- //
    // ------------------------------------------------------ //

    updateEnterpriseAccount: async (uid, data) => {
        return await authToken().then(async (token) => {
            const config = {
                headers: { Authorization: `Bearer ${token}`}
            };

            return await axios.put(`/api/enterprise/accounts/${uid}`, data, config);
        });
    },

    updateEmployeeRole: async (enterprise, uid, data) => {
        return await authToken().then(async (token) => {
            const config = {
                headers: { Authorization: `Bearer ${token}`}
            };

            return await axios.put(`/api/enterprise/employee/role/${enterprise}/${uid}`, data, config);
        });
    },

    updateEnterpriseBrand: async (data) => {
        return await authToken().then(async (token) => {
            const config = {
                headers: { Authorization: `Bearer ${token}`}
            };

            return await axios.put(`/api/enterprise/themes`, data, config);
        });
    },

    updateEnterpriseQR: async (enterprise, data) => {
        return await authToken().then(async (token) => {
            const config = {
                headers: { Authorization: `Bearer ${token}`}
            };
            
            return await axios.put(`/api/enterprise/qr`, data, config);
        });
    },

    updateEnterpriseVcardByUID: async (uid, data) => {
        return await authToken().then(async (token) => {
            const config = {
                headers: { Authorization: `Bearer ${token}`}
            };

            return await axios.put(`/api/enterprise/vcard/${uid}`, data, config);
        });
    },

    updateEnterpriseSkillsByUID: async (data) => {
        return await authToken().then(async (token) => {
            const config = {
                headers: { Authorization: `Bearer ${token}`}
            };

            return await axios.put(`/api/enterprise/skills`, data, config);
        });
    },

    updateBrandBackground: async (route, file, fileName, oldFile) => {
        if (oldFile && oldFile.includes('storage.googleapis.com')) {
            const oldStorageRef = ref(storage, `${route}/${oldFile}`);

            oldStorageRef.exists().then(async (exists) => {
                if (exists[0]) {
                    await deleteObject(oldStorageRef).catch(err => {
                        console.log(err);
                    });
                };
            });

            const storageRef = ref(storage, `${route}/${fileName}`);
            return await uploadBytes(storageRef, file).then(() => true).catch(() => false);
        } else {
            const storageRef = ref(storage, `${route}/${fileName}`);
            return await uploadBytes(storageRef, file);
        };
    },

    updateQrLogo: async (route, file, fileName) => {
        const storageRef = ref(storage, `${route}/${fileName}`);
        return await uploadBytes(storageRef, file);
    },
    
    updateEnterpriseGroupObject: async (data) => {
        return await authToken().then(async (token) => {
            const config = {
                headers: { Authorization: `Bearer ${token}`}
            };

            return await axios.put(`/api/enterprise/groups/update`, data, config);
        });
    },

    updateUserManagerGroups: async (data) => {
        return await authToken().then(async (token) => {
            const config = {
                headers: { Authorization: `Bearer ${token}`}
            };

            return await axios.put(`/api/enterprise/managers/update`, data, config);
        });
    },

    updateEnterpriseAdminStatus: async (data) => {
        return await authToken().then(async (token) => {
            const config = {
                headers: { Authorization: `Bearer ${token}`}
            };

            return await axios.put(`/api/enterprise/admin/update`, data, config);
        });
    },

    updateGroupMember: async (data) => {
        return await authToken().then(async (token) => {
            const config = {
                headers: { Authorization: `Bearer ${token}`}
            };
        
            return await axios.put(`/api/enterprise/group/member/update`, data, config);
        });
    },

    updateEnterpriseUserObject: async (data, uid) => {
        return await authToken().then(async (token) => {
            const config = {
                headers: { Authorization: `Bearer ${token}`}
            };
        
            return await axios.put(`/api/enterprise/users/${uid}`, data, config);
        });
    },

    updateLiquidQrAuth: async (data) => {
        return await authToken().then(async (token) => {
            const config = {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            };

            return await axios.put(`/api/enterprise/u/lqr`, data, config);
        });
    },

    // ------------------------------------------------------ //
    // -------------------END PUT ROUTES--------------------- //
    // ------------------------------------------------------ //

    // ------------------------------------------------------ //
    // ---------------------POST ROUTES---------------------- //
    // ------------------------------------------------------ //

    createNewEnterpriseUser: async (data) => {
        return await authToken().then(async (token) => {
            const config = {
                headers: { Authorization: `Bearer ${token}`}
            };

            return await axios.post(`/api/enterprise/users`, data, config);
        });
    },

    updateEnterpriseTour: async (data) => {
        return await authToken().then(async (token) => {
            const config = {
                headers: { Authorization: `Bearer ${token}`}
            };

            return await axios.post(`/api/enterprise/tour`, data, config);
        });
    },
    
    // ------------------------------------------------------ //
    // --------------------END POST ROUTES------------------- //
    // ------------------------------------------------------ //

    // ------------------------------------------------------ //
    // ---------------------DELETE ROUTES-------------------- //
    // ------------------------------------------------------ //

    deleteEnterpriseUser: async (enterpriseName, uid, username) => {
        return await authToken().then(async (token) => {
            const config = {
                headers: { Authorization: `Bearer ${token}`}
            };

            return await axios.delete(`/api/enterprise/${enterpriseName}/${uid}/${username}`, config);
        });
    }

    // ------------------------------------------------------ //
    // -------------------END DELETE ROUTES------------------ //
    // ------------------------------------------------------ //
};